import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext, apiClient } from '../App';
import DateOfBirthPicker from '../components/DateOfBirthPicker';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  consumeParticipationDobIntent,
  persistParticipationDob,
  readParticipationDobIntent,
} from '../lib/participationDob';

const CompleteDateOfBirth = () => {
  const { user, beginUserUpdate } = useContext(AuthContext);
  const navigate = useNavigate();
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [dobValidation, setDobValidation] = useState({ isValid: false, error: '' });
  const [saving, setSaving] = useState(false);
  const completingRef = useRef(false);

  useEffect(() => {
    if (!user?.date_of_birth || completingRef.current) return;
    const intent = consumeParticipationDobIntent();
    navigate(intent?.returnTo || '/dashboard', {
      replace: true,
      state: intent ? {
        dobCompletionSuccess: true,
        participationIntentType: intent.type,
      } : null,
    });
  }, [navigate, user?.date_of_birth]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || !dateOfBirth || !dobValidation.isValid) return;

    completingRef.current = true;
    setSaving(true);
    try {
      const updated = await persistParticipationDob({
        apiClient,
        dateOfBirth,
        beginUserUpdate,
      });

      if (!updated) return;
      const intent = consumeParticipationDobIntent();
      toast.success('Date of birth saved');
      navigate(intent?.returnTo || '/dashboard', {
        replace: true,
        state: intent ? {
          dobCompletionSuccess: true,
          participationIntentType: intent.type,
        } : null,
      });
    } catch (error) {
      completingRef.current = false;
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Unable to save date of birth');
    } finally {
      setSaving(false);
    }
  };

  const intentAvailable = Boolean(readParticipationDobIntent());

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-10">
      <Card className="mx-auto max-w-md rounded-3xl border-zinc-200 p-6 shadow-sm">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 text-orange-600">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-950">Add your date of birth</h1>
        <p className="mt-2 text-sm leading-6 text-zinc-600">
          We use this privately to confirm that each run is appropriate for your age. Your exact date of birth is not shown to other players.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <DateOfBirthPicker
            value={dateOfBirth}
            onChange={setDateOfBirth}
            onValidationChange={setDobValidation}
            required
            minAge={13}
            maxAge={100}
          />
          <Button
            type="submit"
            disabled={saving || !dateOfBirth || !dobValidation.isValid}
            className="h-12 w-full rounded-xl bg-orange-500 font-semibold text-white hover:bg-orange-600"
            data-testid="save-participation-dob"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Save and return'}
          </Button>
        </form>

        {!intentAvailable && (
          <Button
            type="button"
            variant="ghost"
            className="mt-3 w-full text-zinc-500"
            onClick={() => navigate('/dashboard', { replace: true })}
          >
            Back to dashboard
          </Button>
        )}
      </Card>
    </div>
  );
};

export default CompleteDateOfBirth;
